import React, { useRef, useState } from "react";
import { useTemplate } from "@utils/useTemplate";

interface FileUploadProps {
  onTemplateGenerated: (taskId: string) => void;
  setIsTemplateGenerating: (value: boolean) => void;
  isEditing: boolean;
}

const FileUpload: React.FC<FileUploadProps> = ({
  onTemplateGenerated,
  setIsTemplateGenerating,
  isEditing,
}) => {
  const [error, setError] = useState<string | null>(null);
  const [dragging, setDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const { generateFromFile } = useTemplate();

  const handleFileInputChange = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    if (!isEditing) return;
    const file = event.target.files ? event.target.files[0] : null;
    if (file) {
      await handleFileUpload(file);
    }
  };

  const handleFileUpload = async (file: File) => {
    if (!isEditing) return;
    setIsTemplateGenerating(true);
    setError(null);

    try {
      const taskId = await generateFromFile(file);
      if (taskId) {
        onTemplateGenerated(taskId);
      } else {
        throw new Error("No task ID returned from server");
      }
    } catch (err) {
      console.error("Error uploading file:", err);
      setError("Failed to upload file. Please try again.");
      setIsTemplateGenerating(false);
    }
  };

  const triggerFileInput = () => {
    if (!isEditing) return;
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleDragOver = (event: React.DragEvent) => {
    if (!isEditing) return;
    event.preventDefault();
    setDragging(true);
  };

  const handleDragLeave = () => {
    if (!isEditing) return;
    setDragging(false);
  };

  const handleDrop = (event: React.DragEvent) => {
    if (!isEditing) return;
    event.preventDefault();
    setDragging(false);
    const file = event.dataTransfer.files[0];
    if (file) {
      handleFileUpload(file);
    }
  };

  return (
    <div className="p-4 w-full flex flex-col items-center">
      <h3 className="text-center text-sm 2xl:text-base w-full max-w-lg ">
        You can upload an existing report <br /> and use it to generate a
        template
      </h3>
      <div
        className={`w-full max-w-lg mt-4 border-blue-600 border-2 border-dashed p-6 text-center  rounded-lg transition flex flex-col items-center ${
          dragging ? "bg-blue-50" : "bg-white"
        } ${isEditing ? "" : "opacity-50 cursor-not-allowed"}`}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        <img src="/upload.svg" alt="Upload Icon" className="w-15 h-15 mb-3" />
        <p className="text-m text-gray-600 mb-3 text-sm 2xl:text-base">
          Drag your file to generate
        </p>
        <p className="text-xs 2xl:text-sm text-gray-600">OR</p>
        <button
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            triggerFileInput();
          }}
          className={`mt-2 py-2 px-4 text-sm 2xl:text-base rounded-md font-bold transition flex items-center justify-center border  bg-transparent ${
            isEditing
              ? "text-blue-600 border-blue-600 hover:bg-gray-100"
              : "text-gray-400 cursor-not-allowed"
          }`}
          disabled={!isEditing}
        >
          <img
            src="/upload-icon.svg"
            alt="Upload Icon"
            className="w-5 h-5 mr-2"
          />
          Upload
        </button>
      </div>
      <input
        type="file"
        ref={fileInputRef}
        data-testid="file-input"
        className="hidden"
        accept=".pdf,.doc,.docx,.ppt,.pptx,.xls,.xlsx"
        onChange={handleFileInputChange}
        disabled={!isEditing}
      />
      {error && <p className="text-red-600 text-sm mt-2">{error}</p>}
    </div>
  );
};

export default FileUpload;
